/**
 * `@marvr/storyblok-rich-text-vue-renderer` drops `marks` on image nodes.
 * Storyblok stores image alignment as a `styled` mark (e.g. `class: "text-center block"`).
 * Lift that class onto `attrs.class` so the image resolver can react to it.
 */
export function applyRichTextImageMarks<T>(node: T): T {
	return mapNode(node as unknown) as T;
}

function mapNode(node: unknown): unknown {
	if (!node || typeof node !== 'object') {
		return node;
	}

	if (Array.isArray(node)) {
		return node.map(mapNode);
	}

	const current = node as {
		type?: string
		attrs?: Record<string, unknown> & {class?: string}
		content?: unknown[]
		marks?: Array<{type?: string; attrs?: {class?: string}}>
	};

	const content = current.content?.map(mapNode);
	const contentChanged = Boolean(
		content
		&& current.content
		&& content.some((child, index) => child !== current.content![index]),
	);

	if (current.type === 'image' && current.marks?.length) {
		const styledClass = current.marks
			.filter((mark) => mark.type === 'styled' && mark.attrs?.class)
			.map((mark) => mark.attrs!.class as string)
			.join(' ');

		if (styledClass) {
			return {
				type: current.type,
				attrs: {
					...current.attrs,
					class: [styledClass, current.attrs?.class].filter(Boolean).join(' '),
				},
			};
		}
	}

	if (contentChanged) {
		return {
			...current,
			content,
		};
	}

	return current;
}

export function hasRichTextClass(className: string | undefined, token: string): boolean {
	return Boolean(className?.split(/\s+/).includes(token));
}
